import type { components } from "@/api-schema";
import { ContactLink } from "@/components/contact-link/contact-link";
import { CopyToClipboard } from "@/components/copy-to-clipboard/copy-to-clipboard";
import { AddressBlock } from "@/components/detail-page/_components/address-block";
import { DetailTabCard } from "@/components/detail-page/_components/tab/detail-tab-card";
import { DetailTabGrid } from "@/components/detail-page/_components/tab/detail-tab-grid";
import { DetailTabRow } from "@/components/detail-page/_components/tab/detail-tab-row";
import { IdCardIcon, MailIcon, MapPin, PhoneIcon, Smartphone } from "lucide-react";
import type { ReactElement, ReactNode } from "react";
import { useTranslation } from "react-i18next";

type User = components["schemas"]["User"];

export function OverviewTab({ user }: { user: User }): ReactElement {
  const { t } = useTranslation(["masterdata", "common"]);
  const address = user.address;
  const addressText = [
    [address?.street, address?.houseNumber, address?.houseExtension].filter(Boolean).join(" "),
    [address?.postalCode, address?.city].filter(Boolean).join(" "),
  ]
    .filter(Boolean)
    .join(", ");

  return (
    <DetailTabGrid>
      <DetailTabCard icon={MailIcon} title={t("masterdata:ContactInformation")}>
        <DetailTabRow
          label={t("common:Email")}
          value={
            user.email ? (
              <WithCopy value={user.email} tooltip={t("masterdata:CopyEmail")}>
                <ContactLink icon={MailIcon} label={user.email} href={`mailto:${user.email}`} />
              </WithCopy>
            ) : undefined
          }
        />
        <DetailTabRow
          label={t("common:Phone")}
          value={
            user.telephone ? (
              <WithCopy value={user.telephone} tooltip={t("masterdata:CopyPhoneNumber")}>
                <ContactLink icon={PhoneIcon} label={user.telephone} href={`tel:${user.telephone}`} />
              </WithCopy>
            ) : undefined
          }
        />
        <DetailTabRow
          label={t("common:Mobile")}
          value={
            user.mobile ? (
              <WithCopy value={user.mobile} tooltip={t("masterdata:CopyMobileNumber")}>
                <ContactLink icon={Smartphone} label={user.mobile} href={`tel:${user.mobile}`} />
              </WithCopy>
            ) : undefined
          }
        />
      </DetailTabCard>

      <DetailTabCard icon={IdCardIcon} title={t("masterdata:UserInformation")}>
        <DetailTabRow
          label={t("masterdata:EmployeeNumber")}
          value={user.employeeId ? <WithCopy value={user.employeeId}>{user.employeeId}</WithCopy> : undefined}
          mono
        />
        <DetailTabRow label={t("masterdata:MainBranch")} value={user.mainBranch?.name} />
        <DetailTabRow
          label={t("masterdata:Specialization")}
          value={
            user.specialization ? (
              <WithCopy value={user.specialization} tooltip={t("masterdata:CopySpecialization")}>
                {user.specialization}
              </WithCopy>
            ) : undefined
          }
        />
      </DetailTabCard>

      <DetailTabCard icon={MapPin} title={t("masterdata:Address")}>
        <div className="flex items-start justify-between gap-2 py-1.5">
          <AddressBlock address={address ?? {}} />
          {addressText && <CopyToClipboard value={addressText} tooltip={t("masterdata:CopyAddress")} />}
        </div>
      </DetailTabCard>
    </DetailTabGrid>
  );
}

function WithCopy({ value, tooltip, children }: { value: string; tooltip?: string; children: ReactNode }) {
  return (
    <span className="inline-flex items-center gap-1">
      {children}
      <CopyToClipboard value={value} tooltip={tooltip} />
    </span>
  );
}
